import React, { useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Plus, Scale, Trash2 } from 'lucide-react';
import {
  Alert, Button, Card, Empty, Field, Input, Page, PageHead, Stat,
} from '../components/ui';
import { n1, shortDate } from '../lib/format';

const MEASURES = [
  { key: 'weight', label: 'Weight', unit: 'kg', required: true },
  { key: 'body_fat', label: 'Body fat', unit: '%' },
  { key: 'chest', label: 'Chest', unit: 'cm' },
  { key: 'waist', label: 'Waist', unit: 'cm' },
  { key: 'hips', label: 'Hips', unit: 'cm' },
  { key: 'arms', label: 'Arms', unit: 'cm' },
  { key: 'thighs', label: 'Thighs', unit: 'cm' },
];

const axis = { stroke: 'var(--text-faint)', fontSize: 11, tickLine: false, axisLine: false };
const tip = {
  background: 'var(--surface-2)', border: '1px solid var(--border-strong)',
  borderRadius: 10, fontSize: 12, color: 'var(--text)',
};

export default function Weight({ weightLog, onAdd, onDelete }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.weight) return;
    setBusy(true); setError('');
    try {
      const payload = { weight: parseFloat(form.weight) };
      MEASURES.slice(1).forEach((m) => {
        payload[m.key] = form[m.key] ? parseFloat(form[m.key]) : null;
      });
      await onAdd(payload);
      setForm({});
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const series = weightLog.map((w) => ({
    date: shortDate(w.logged_at),
    weight: w.weight,
    bodyFat: w.body_fat,
    waist: w.waist,
  }));

  const latest = weightLog[weightLog.length - 1];
  const first = weightLog[0];
  const change = latest && first ? latest.weight - first.weight : null;
  const hasBodyFat = weightLog.some((w) => w.body_fat != null);

  return (
    <Page>
      <PageHead title="Weight & Measurements">
        Track body weight alongside composition and tape measurements.
      </PageHead>

      <div className="grid grid-4">
        <Stat label="Latest" value={latest ? n1(latest.weight) : '–'} unit="kg"
          note={latest ? shortDate(latest.logged_at) : 'No entries'} />
        <Stat label="Change" value={change != null ? `${change > 0 ? '+' : ''}${n1(change)}` : '–'}
          unit="kg" note="since first entry" tone={change != null && change < 0 ? 'ok' : undefined} />
        <Stat label="Body fat" value={latest?.body_fat != null ? n1(latest.body_fat) : '–'} unit="%" />
        <Stat label="Entries" value={weightLog.length} />
      </div>

      <Card title="Record a weigh-in">
        <form onSubmit={submit}>
          {error && <div style={{ marginBottom: 12 }}><Alert tone="crit">{error}</Alert></div>}
          <div className="grid grid-4">
            {MEASURES.map((m) => (
              <Field key={m.key} label={`${m.label} · ${m.unit}`}>
                <Input numeric type="number" step="any" min="0"
                  placeholder={m.required ? 'Required' : '–'}
                  required={m.required}
                  value={form[m.key] ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, [m.key]: e.target.value }))} />
              </Field>
            ))}
          </div>
          <Button variant="primary" icon={Plus} type="submit" disabled={busy || !form.weight}
            loading={busy} style={{ marginTop: 16 }}>
            Save entry
          </Button>
        </form>
      </Card>

      <Card title="Trend" sub={hasBodyFat ? 'Weight and body fat over time' : 'Weight over time'}>
        {weightLog.length < 2 ? (
          <Empty icon={Scale} title="Log at least two entries">
            The chart appears once there is something to compare.
          </Empty>
        ) : (
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 6, right: 12, left: -14, bottom: 0 }}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" {...axis} minTickGap={30} />
                <YAxis yAxisId="kg" {...axis} domain={['dataMin - 1', 'dataMax + 1']} width={42}
                  tickFormatter={(v) => v.toFixed(0)} />
                {hasBodyFat && (
                  <YAxis yAxisId="pct" orientation="right" {...axis} width={36}
                    domain={['dataMin - 2', 'dataMax + 2']} tickFormatter={(v) => v.toFixed(0)} />
                )}
                <Tooltip contentStyle={tip} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line yAxisId="kg" type="monotone" dataKey="weight" name="Weight (kg)"
                  stroke="var(--accent)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                {hasBodyFat && (
                  <Line yAxisId="pct" type="monotone" dataKey="bodyFat" name="Body fat (%)"
                    stroke="var(--ok)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card title="History" flush>
        {weightLog.length === 0 ? (
          <Empty icon={Scale} title="No entries yet" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Date</th>
                  {MEASURES.map((m) => <th key={m.key} className="n">{m.label}</th>)}
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...weightLog].reverse().map((w) => (
                  <tr key={w.id}>
                    <td>{shortDate(w.logged_at)}</td>
                    {MEASURES.map((m) => (
                      <td key={m.key} className="n">{w[m.key] != null ? n1(w[m.key]) : '–'}</td>
                    ))}
                    <td className="act">
                      <Button size="sm" variant="danger" icon={Trash2}
                        onClick={() => onDelete(w.id)} aria-label="Delete entry" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  );
}
